import { Link } from "react-router-dom";

type FooterLink = {
  label: string;
  to?: string;
  href?: string;
  external?: boolean;
};

const columns: { title: string; links: FooterLink[] }[] = [
  {
    title: "Platform",
    links: [
      { label: "Product", to: "/product" },
      { label: "Architecture", to: "/architecture" },
      { label: "Pricing", to: "/pricing" },
      { label: "Download", to: "/get-mane" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Integrations", to: "/integrations" },
      { label: "Example Library", to: "/examples" },
      { label: "Docs", to: "/docs" },
      { label: "Learn", to: "/learn" },
      { label: "Blog", to: "/blog" },
      { label: "Changelog", to: "/changelog" },
      { label: "All Agents", to: "/agents" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", to: "/about" },
      { label: "Contact", to: "/contact" },
      { label: "Privacy", to: "/privacy" },
      { label: "Terms", to: "/terms" },
    ],
  },
  {
    title: "Connect",
    links: [
      { label: "X / Twitter", href: "#", external: true },
      { label: "LinkedIn", href: "#", external: true },
      { label: "hello@mane.dev", href: "mailto:hello@mane.dev", external: true },
    ],
  },
];

const isExternal = (l: FooterLink) =>
  l.external || (l.href ? /^(https?:)?\/\//i.test(l.href) : false);

const ExternalGlyph = () => (
  <span
    aria-hidden
    className="inline-block ml-1 align-baseline"
    style={{ fontSize: 10, color: "hsl(var(--cream) / 0.6)" }}
  >
    ↗
  </span>
);

const Footer = () => (
  <footer
    className="footer-scope relative"
    style={
      {
        backgroundColor: "hsl(240 14% 3%)",
        // Scoped cream token, pure white, the existing brand cream value.
        ["--cream" as string]: "0 0% 100%",
        color: "hsl(var(--cream))",
      } as React.CSSProperties
    }
  >
    {/* Top hairline + diagonal hatch band (cream-tinted, scoped via .footer-scope) */}
    <div
      aria-hidden
      className="h-px w-full"
      style={{ backgroundColor: "hsl(var(--cream) / 0.2)" }}
    />
    <div
      aria-hidden
      className="footer-hatch w-full"
      style={{ height: 24 }}
    />

    {/* Scoped CSS: override .bg-diagonal-hatch stripe color inside the footer
        to cream / 0.25, and underline-on-hover styling for footer links. */}
    <style>{`
      .footer-scope .footer-hatch {
        background: repeating-linear-gradient(
          135deg,
          hsl(var(--cream) / 0.25) 0 1px,
          transparent 1px 7px
        );
      }
      .footer-scope .footer-hatch-overlay {
        background: repeating-linear-gradient(
          135deg,
          hsl(var(--cream) / 0.25) 0 1px,
          transparent 1px 7px
        );
      }
      .footer-scope a.footer-link {
        text-decoration: none;
        color: hsl(var(--cream) / 0.85);
      }
      .footer-scope a.footer-link:hover {
        text-decoration: underline;
        text-decoration-color: hsl(var(--cream) / 0.6);
        text-underline-offset: 3px;
      }
    `}</style>

    <div className="max-w-content mx-auto px-5 sm:px-8 py-16 sm:py-20">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-10 mb-16">
        {columns.map((col) => (
          <div key={col.title}>
            <p
              className="font-sans uppercase mb-5"
              style={{
                fontSize: 13,
                letterSpacing: "0.18em",
                fontWeight: 400,
                color: "hsl(var(--cream) / 0.65)",
              }}
            >
              {col.title}
            </p>
            <div className="space-y-3">
              {col.links.map((link) => {
                const ext = isExternal(link);
                const content = (
                  <>
                    {link.label}
                    {ext && <ExternalGlyph />}
                  </>
                );
                const baseClass =
                  "footer-link block font-sans text-[14px] font-normal";
                return link.to ? (
                  <Link key={link.label} to={link.to} className={baseClass}>
                    {content}
                  </Link>
                ) : (
                  <a
                    key={link.label}
                    href={link.href}
                    {...(ext
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                    className={baseClass}
                  >
                    {content}
                  </a>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Hairline before legal row */}
      <div
        aria-hidden
        className="h-px w-full"
        style={{ backgroundColor: "hsl(var(--cream) / 0.2)" }}
      />

      <div className="pt-6">
        <div
          className="flex flex-wrap items-center gap-x-3 gap-y-1 font-sans text-[12px] font-normal"
          style={{ color: "hsl(var(--cream) / 0.65)" }}
        >
          <span>© {new Date().getFullYear()} Mane.dev</span>
          <span>·</span>
          <Link to="/terms" className="footer-link">Terms</Link>
          <span>·</span>
          <Link to="/privacy" className="footer-link">Privacy</Link>
          <span>·</span>
          <span>Claude Code</span>
          <span>·</span>
          <span>Codex</span>
          <span>·</span>
          <span>Claude Agent SDK</span>
          <span>·</span>
          <span>Model Context Protocol</span>
        </div>
      </div>
    </div>

    {/* Watermark band, 320px tall, layered backgrounds + centered wordmark */}
    <div
      className="relative w-full"
      style={{ height: 320, overflow: "hidden" }}
    >
      {/* Layer 1: tiled z-watermark @ 0.06 */}
      <div
        aria-hidden
        className="absolute inset-0 bg-z-watermark pointer-events-none"
        style={{ opacity: 0.06 }}
      />
      {/* Layer 2: diagonal hatch overlay @ 0.04 (scoped cream stripes) */}
      <div
        aria-hidden
        className="footer-hatch-overlay absolute inset-0 pointer-events-none"
        style={{ opacity: 0.04 }}
      />
      {/* Layer 3: centered Mane wordmark @ 720px wide, cream, opacity 0.08.
          TODO: swap inline SVG for the real Mane wordmark asset when available. */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <span
          aria-hidden
          className="select-none leading-none"
          style={{
            fontFamily: "'Inter', system-ui, sans-serif",
            fontWeight: 900,
            fontSize: "clamp(140px, 28vw, 360px)",
            letterSpacing: "-0.055em",
            backgroundImage:
              "linear-gradient(to bottom, hsl(var(--cream) / 0.22) 0%, hsl(var(--cream) / 0.10) 45%, hsl(var(--cream) / 0.02) 100%)",
            WebkitBackgroundClip: "text",
            backgroundClip: "text",
            WebkitTextFillColor: "transparent",
            color: "transparent",
          }}
        >
          mane
        </span>
      </div>

      {/* Bottom-centered 56×6 pill */}
      <div
        aria-hidden
        className="absolute left-1/2 -translate-x-1/2 pointer-events-none"
        style={{
          bottom: 16,
          width: 56,
          height: 6,
          borderRadius: 999,
          backgroundColor: "hsl(var(--cream) / 0.35)",
        }}
      />
    </div>
  </footer>
);

export default Footer;
